// One pallet status transition — the write behind the run list's card actions
// AND the pallet page's "Place this pallet" (ui-flow UF-08, 30 Sep 2026). Moved
// here out of Pallets.tsx so both surfaces write the same row the same way:
// the stamps each status carries, the version-guarded update, and the two
// operator sentences a failed placement needs.
import { ValidationError } from '@/lib/errorReporter'
import { updateWithVersion, isStaleWriteError } from '@/lib/optimistic'
import { coldroomCellCollisionMessage } from '@/lib/floorMapMove'
import { placeOnDeletedRunRefusal } from '@/lib/packhouseErrorDomains'

export type PalletTransitionStatus =
  | 'IN_PRODUCTION'
  | 'IN_COLDROOM'
  | 'IN_LOADING_AREA'
  | 'INSPECTED_APPROVED'
  | 'INSPECTED_REJECTED'
  | 'DISPATCHED'
  | 'RETURNED_FRESHMARK'

export interface PalletTransitionExtra {
  rejection_reason?:  string
  coldroom_grid?:     string | null
  coldroom_overflow?: boolean
  coldroom_id?:       string
  loading_bay_lane?:  string
}

/** The columns a move to `toStatus` writes. Pure, so the rules are pinned by a test. */
export function transitionUpdates(
  toStatus: PalletTransitionStatus,
  extra: PalletTransitionExtra | undefined,
  meId: string,
  nowIso: string,
): Record<string, unknown> {
  const updates: Record<string, unknown> = { status: toStatus }
  if (toStatus === 'IN_COLDROOM') {
    updates.stocked_at    = nowIso
    updates.stocked_by    = meId
    updates.coldroom_grid = extra?.coldroom_grid ?? null
    updates.coldroom_id   = extra?.coldroom_id   ?? null
    // Written on EVERY coldroom transition, not only the overflow ones.
    // `?? false` is doing real work: a pallet coming back to cold with a
    // cell must not inherit a TRUE from a previous overflow stay, which is
    // the state the pallets_overflow_has_no_cell CHECK refuses.
    updates.coldroom_overflow = extra?.coldroom_overflow ?? false
  }
  if (toStatus === 'IN_LOADING_AREA') {
    updates.loading_bay_lane = extra?.loading_bay_lane ?? null
  }
  if (toStatus === 'DISPATCHED')         { updates.dispatched_at = nowIso; updates.dispatched_by = meId }
  if (toStatus === 'INSPECTED_APPROVED') { updates.inspection_result = 'APPROVED'; updates.inspected_at = nowIso; updates.inspected_by = meId }
  if (toStatus === 'INSPECTED_REJECTED') {
    updates.inspection_result = 'REJECTED'
    updates.inspected_at = nowIso
    updates.inspected_by = meId
    updates.rejection_reason = extra?.rejection_reason ?? null
  }
  if (toStatus === 'RETURNED_FRESHMARK') {
    updates.rejection_reason = extra?.rejection_reason ?? null
  }
  return updates
}

/**
 * The operator-facing Error for a failed pallet transition, or null when the
 * failure keeps its own text. Pure: a stale write, a cell race, a cell clash
 * on a coldroom move, or a placement onto a deleted run (42501 from
 * pallets_placement_guard, not a role problem).
 */
function transitionErrorFor(e: unknown, toStatus: PalletTransitionStatus): Error | null {
  const err = e as { code?: string; message?: string } | null
  if (isStaleWriteError(e)) {
    return new ValidationError('Someone else just updated this pallet. Refresh to see the latest state and try again.')
  }
  // Race condition on cell uniqueness — give the operator a clear hint.
  if (err?.code === '23505' && err?.message?.includes('pallets_coldroom_cell_unique')) {
    return new ValidationError('That cell was just taken by another pallet. Pick a different one.')
  }
  if (toStatus !== 'IN_COLDROOM') return null
  // Any other cell clash (occupancy trigger 23P01 and friends) on a coldroom move.
  const msg = coldroomCellCollisionMessage(e) ?? placeOnDeletedRunRefusal(e)
  return msg ? new ValidationError(msg) : null
}

/**
 * Move one pallet to `toStatus`. Optimistic concurrency: the version the
 * client read goes in the WHERE clause, so another operator's edit between
 * the read and the tap is refused, never trampled.
 */
export async function transitionPallet({ pallet, toStatus, extra, meId, skipWeighGate = false }: {
  pallet:   { pallet_id: string; version: number; awaiting_weight?: boolean | null }
  toStatus: PalletTransitionStatus
  extra?:   PalletTransitionExtra
  meId:     string
  /** A pallet that arrived already packed (an EXTERNAL_RECEIPT run) may carry
   *  awaiting_weight yet is placed without the weigh rule, as Receive packed
   *  pallets does (F05 UF-01). Never set it for a pallet built here. */
  skipWeighGate?: boolean
}): Promise<void> {
  // A shell (created weightless from a line) must be weighed before it can
  // move out of production — never let an unweighed pallet get approved or
  // dispatched. The weigh dialog clears awaiting_weight.
  if (pallet.awaiting_weight && !skipWeighGate) {
    throw new ValidationError('Weigh this pallet first — it was created from a line without a weight. Tap "Weigh" to capture the gross weight.')
  }
  try {
    await updateWithVersion('pallets',
      { col: 'pallet_id', value: pallet.pallet_id },
      pallet.version,
      transitionUpdates(toStatus, extra, meId, new Date().toISOString()),
    )
  } catch (e: unknown) {
    throw transitionErrorFor(e, toStatus) ?? e
  }
}
